import type { InviteStatus } from "@/features/identity-hub/api/identity-hub.api";

export type UserStatus = "ACTIVE" | "PENDING" | "INACTIVE" | "DEACTIVATED";

export interface StatusVisualStyle {
  dot: string;
  pill: string;
  text: string;
  border: string;
}

export interface StatusVisual extends StatusVisualStyle {
  label: string;
  desc: string;
}

type TranslateFn = (key: string, defaultValue: string) => string;

/**
 * Single source of truth for user-status colors. Previously diverged
 * across the People table, Person drawer, and the Org/Role drawer member
 * rows (same colors, different shapes/labels) — now every surface renders
 * from this map via `StatusBadge`.
 */
const USER_STATUS_STYLES: Record<UserStatus, StatusVisualStyle> = {
  ACTIVE: {
    dot: "bg-success",
    pill: "bg-success-subtle",
    text: "text-success-subtle-foreground",
    border: "border-success-border",
  },
  PENDING: {
    dot: "bg-warning",
    pill: "bg-warning-subtle",
    text: "text-warning-subtle-foreground",
    border: "border-warning-border",
  },
  INACTIVE: {
    dot: "bg-muted-foreground",
    pill: "bg-muted",
    text: "text-muted-foreground",
    border: "border-border",
  },
  DEACTIVATED: {
    dot: "bg-destructive",
    pill: "bg-destructive/10",
    text: "text-destructive",
    border: "border-destructive/20",
  },
};

const USER_STATUS_LABEL_KEYS: Record<UserStatus, string> = {
  ACTIVE: "iam.personDrawer.status.ACTIVE",
  PENDING: "iam.personDrawer.status.PENDING",
  INACTIVE: "iam.personDrawer.status.INACTIVE",
  DEACTIVATED: "iam.personDrawer.status.DEACTIVATED",
};

const USER_STATUS_DESC_KEYS: Record<UserStatus, string> = {
  ACTIVE: "iam.personDrawer.statusDescription.ACTIVE",
  PENDING: "iam.personDrawer.statusDescription.PENDING",
  INACTIVE: "iam.personDrawer.statusDescription.INACTIVE",
  DEACTIVATED: "iam.personDrawer.statusDescription.DEACTIVATED",
};

const USER_STATUS_LABEL_DEFAULTS: Record<UserStatus, string> = {
  ACTIVE: "Active",
  PENDING: "Invited",
  INACTIVE: "Inactive",
  DEACTIVATED: "Suspended",
};

const USER_STATUS_DESC_DEFAULTS: Record<UserStatus, string> = {
  ACTIVE: "This person has accepted their invitation and can sign in and use the platform.",
  PENDING: "An invitation has been sent but not yet accepted. They can't sign in until they complete sign-up.",
  INACTIVE: "This account has no active access and can't sign in.",
  DEACTIVATED: "An admin has suspended this account. They can't sign in until it's reactivated.",
};

function resolveUserStatus(status: string): UserStatus {
  return status in USER_STATUS_STYLES ? (status as UserStatus) : "INACTIVE";
}

export function translateUserStatusVisual(t: TranslateFn, status: string): StatusVisual {
  const key = resolveUserStatus(status);
  const style = USER_STATUS_STYLES[key];
  return {
    ...style,
    label: t(USER_STATUS_LABEL_KEYS[key], USER_STATUS_LABEL_DEFAULTS[key]),
    desc: t(USER_STATUS_DESC_KEYS[key], USER_STATUS_DESC_DEFAULTS[key]),
  };
}

/** Compact `"bg text border"` class string for contexts that render their own badge markup (drawer member rows). */
export function statusBadgeClass(status: string): string {
  const cfg = USER_STATUS_STYLES[resolveUserStatus(status)];
  return `${cfg.pill} ${cfg.text} ${cfg.border}`;
}

export interface InviteStatusVisualStyle {
  dot: string;
  pill: string;
  text: string;
}

export interface InviteStatusVisual extends InviteStatusVisualStyle {
  label: string;
  desc: string;
}

const INVITE_STATUS_STYLES: Record<InviteStatus, InviteStatusVisualStyle> = {
  pending: {
    dot: "bg-amber-500",
    pill: "bg-amber-50 ring-amber-200/70 dark:bg-amber-950/30 dark:ring-amber-800/40",
    text: "text-amber-700 dark:text-amber-200",
  },
  expired: {
    dot: "bg-rose-500",
    pill: "bg-rose-50 ring-rose-200/70 dark:bg-rose-950/30 dark:ring-rose-800/40",
    text: "text-rose-700 dark:text-rose-200",
  },
  accepted: {
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 ring-emerald-200/70 dark:bg-emerald-950/30 dark:ring-emerald-800/40",
    text: "text-emerald-700 dark:text-emerald-200",
  },
};

const INVITE_STATUS_LABEL_KEYS: Record<InviteStatus, string> = {
  pending: "iam.invites.statusPill.pending.label",
  expired: "iam.invites.statusPill.expired.label",
  accepted: "iam.invites.statusPill.accepted.label",
};

const INVITE_STATUS_DESC_KEYS: Record<InviteStatus, string> = {
  pending: "iam.invites.statusPill.pending.desc",
  expired: "iam.invites.statusPill.expired.desc",
  accepted: "iam.invites.statusPill.accepted.desc",
};

const INVITE_STATUS_LABEL_DEFAULTS: Record<InviteStatus, string> = {
  pending: "Pending",
  expired: "Expired",
  accepted: "Accepted",
};

const INVITE_STATUS_DESC_DEFAULTS: Record<InviteStatus, string> = {
  pending: "The invite has been sent and is waiting for the recipient to accept. It stays valid until it expires.",
  expired: "The invite lapsed before it was accepted. Resend it to give the recipient a fresh link.",
  accepted: "The recipient accepted the invite and now has an account with the granted access.",
};

export function translateInviteStatusVisual(t: TranslateFn, status: InviteStatus): InviteStatusVisual {
  const style = INVITE_STATUS_STYLES[status];
  return {
    ...style,
    label: t(INVITE_STATUS_LABEL_KEYS[status], INVITE_STATUS_LABEL_DEFAULTS[status]),
    desc: t(INVITE_STATUS_DESC_KEYS[status], INVITE_STATUS_DESC_DEFAULTS[status]),
  };
}
